import ClayButton from '@clayui/button';
import ClayDatePicker from '@clayui/date-picker';
import ClayForm, {ClaySelect} from '@clayui/form';
import React, {useMemo, useRef, useState} from 'react';
import {createRoot} from 'react-dom/client';

import bannerPatternDesktopImage from './assets/banner-pattern-desktop.png';
import bannerPatternMobileImage from './assets/banner-pattern-mobile.png';
import calendarIcon from './assets/Icon-calendar.svg';
import caretDoubleIcon from './assets/Icon-caret-double.svg';
import reloadIcon from './assets/icon_reload.svg';
import trimesterOneImage from './assets/trimester-1.png';
import trimesterTwoImage from './assets/trimester-2.png';
import trimesterThreeImage from './assets/trimester-3.png';
import {
	TRANSFER_TYPES,
	addDays,
	calculateDueDate,
	formatDate,
	getTodayDateOnly,
	isValidDateOnly,
	parseDateOnly,
	toDateOnlyString,
} from './dateUtils';
import {formatGestationalAgeMessages, getLocale, translate} from './i18n';
import './styles.css';

const ELEMENT_ID = 'due-date-calculator';
const TRIMESTER_IMAGES = [trimesterOneImage, trimesterTwoImage, trimesterThreeImage];
const CLIENT_EXTENSION_BASE_PATH = '/o/ivf-due-date-calculator/';

function t(key, replacements) {
	return translate(key, replacements);
}

function validateForm(dateValue, transferTypeValue) {
	const errors = {};

	if (!dateValue) {
		errors.date = t('due-date-calculator.error.date.required');
	}
	else if (!isValidDateOnly(dateValue)) {
		errors.date = t('due-date-calculator.error.date.invalid');
	}

	if (!transferTypeValue) {
		errors.transferType = t('due-date-calculator.error.transfer-type.required');
	}

	return errors;
}

function getSpritemap() {
	const themeImagesPath = window.Liferay?.ThemeDisplay?.getPathThemeImages?.();

	return themeImagesPath
		? `${themeImagesPath}/lexicon/icons.svg`
		: '/o/classic-theme/images/lexicon/icons.svg';
}

function getClientExtensionAssetURL(assetPath) {
	if (/^(?:data:|https?:\/\/)/.test(assetPath)) {
		return assetPath;
	}

	const normalizedAssetPath = assetPath
		.replace(/^\.\//, '')
		.replace(/^\//, '')
		.replace(/^static\//, '');
	const portalURL = window.Liferay?.ThemeDisplay?.getPortalURL?.() || '';

	return `${portalURL}${CLIENT_EXTENSION_BASE_PATH}${normalizedAssetPath}`;
}

function App() {
	const locale = getLocale();
	const rangeSeparator = t('due-date-calculator.result.range-separator');
	const datePickerSpritemap = getSpritemap();
	const icons = useMemo(
		() => ({
			calendar: getClientExtensionAssetURL(calendarIcon),
			caretDouble: getClientExtensionAssetURL(caretDoubleIcon),
			reload: getClientExtensionAssetURL(reloadIcon),
		}),
		[]
	);
	const iconStyles = useMemo(
		() => ({
			'--ivirma-ivf-calendar-icon': `url("${icons.calendar}")`,
			'--ivirma-ivf-caret-double-icon': `url("${icons.caretDouble}")`,
		}),
		[icons]
	);
	const bannerPatterns = useMemo(
		() => ({
			desktop: getClientExtensionAssetURL(bannerPatternDesktopImage),
			mobile: getClientExtensionAssetURL(bannerPatternMobileImage),
		}),
		[]
	);
	const trimesterImages = useMemo(
		() => TRIMESTER_IMAGES.map(getClientExtensionAssetURL),
		[]
	);
	const todayValue = useMemo(() => toDateOnlyString(getTodayDateOnly()), []);
	const [dateValue, setDateValue] = useState(todayValue);
	const [transferType, setTransferType] = useState('');
	const [errors, setErrors] = useState({});
	const [result, setResult] = useState(null);
	const dateFieldRef = useRef(null);
	const transferTypeRef = useRef(null);
	const formTitleRef = useRef(null);
	const resultRef = useRef(null);

	const resultDates = useMemo(() => {
		if (!result) {
			return [];
		}

		return [-2, -1, 0, 1, 2].map((offset) =>
			addDays(result.estimatedDueDate, offset)
		);
	}, [result]);

	const handleSubmit = (event) => {
		event.preventDefault();

		const nextErrors = validateForm(dateValue, transferType);
		setErrors(nextErrors);

		if (Object.keys(nextErrors).length) {
			window.requestAnimationFrame(() => {
				if (nextErrors.transferType) {
					transferTypeRef.current?.focus();
				}
				else {
					dateFieldRef.current?.focus();
				}
			});
			return;
		}

		const calculatedResult = calculateDueDate(
			parseDateOnly(dateValue),
			transferType
		);

		setResult(calculatedResult);

		window.requestAnimationFrame(() => {
			resultRef.current?.focus();
		});
	};

	const handleRecalculate = () => {
		setResult(null);
		setErrors({});

		window.requestAnimationFrame(() => {
			formTitleRef.current?.focus();
		});
	};

	if (result) {
		const accessibleDate = formatDate(result.estimatedDueDate, locale, 'long');
		const gestationalAge = formatGestationalAgeMessages(result, t);

		return (
			<div className="ivirma-ivf-root">
				<section
					aria-live="polite"
					className="ivirma-ivf ivirma-ivf--result"
					ref={resultRef}
					style={iconStyles}
					tabIndex="-1"
				>
					<div className="ivirma-ivf-result-panel">
					<p className="ivirma-ivf-sr-only">
						{t('due-date-calculator.result.accessible-date', {
							date: accessibleDate,
						})}
					</p>
					<h2>{t('due-date-calculator.result.title')}</h2>

					<div className="ivirma-ivf-date-strip" aria-hidden="true">
						{resultDates.map((dateOnly, index) => (
							<div
								className={index === 2 ? 'ivirma-ivf-date-strip__current' : ''}
								key={toDateOnlyString(dateOnly)}
							>
								{index === 2 ? (
									accessibleDate
								) : (
									<>
										<span className="ivirma-ivf-date-strip__month">
											{formatDate(dateOnly, locale, 'shortMonth')}
										</span>
										<span className="ivirma-ivf-date-strip__day">
											{formatDate(dateOnly, locale, 'day')}
										</span>
									</>
								)}
							</div>
						))}
					</div>

					<p className="ivirma-ivf-result-panel__month">
						{formatDate(result.estimatedDueDate, locale, 'month')}
					</p>
					<p className="ivirma-ivf-result-panel__day">
						{formatDate(result.estimatedDueDate, locale, 'day')}
					</p>
					<p className="ivirma-ivf-result-panel__year">
						{formatDate(result.estimatedDueDate, locale, 'year')}
					</p>

					<ClayButton
						className="ivirma-ivf-button ivirma-ivf-button--small"
						displayType="primary"
						onClick={handleRecalculate}
						type="button"
					>
						<img
							alt=""
							aria-hidden="true"
							className="ivirma-ivf-button__icon"
							src={icons.reload}
						/>
						{t('due-date-calculator.action.recalculate')}
					</ClayButton>
				</div>

				<p className="ivirma-ivf-gestational-age">
					<span>{gestationalAge.primary}</span>
					{gestationalAge.secondary ? (
						<span>{gestationalAge.secondary}</span>
					) : null}
				</p>

				<div className="ivirma-ivf-trimesters">
					{result.trimesterRanges.map((trimester, index) => (
						<article
							className="ivirma-ivf-trimester-card"
							key={trimester.labelKey}
						>
							<img alt="" aria-hidden="true" src={trimesterImages[index]} />
							<div className="ivirma-ivf-trimester-card__content">
								<h3>
									{formatDate(trimester.from, locale, 'shortMonthDay')}
									<br />
									{rangeSeparator}{' '}
									{formatDate(trimester.to, locale, 'shortMonthDay')}
								</h3>
								<p>{t(trimester.labelKey)}</p>
							</div>
						</article>
					))}
					</div>
				</section>
			</div>
		);
	}

	const hasErrors = Object.keys(errors).length > 0;

	return (
		<div className="ivirma-ivf-root">
			<section className="ivirma-ivf" style={iconStyles}>
				<form className="ivirma-ivf-form-panel" noValidate onSubmit={handleSubmit}>
				<div className="ivirma-ivf-form">
					<h2 ref={formTitleRef} tabIndex="-1">
						{t('due-date-calculator.form.title.line-1')}
						<br />
						{t('due-date-calculator.form.title.line-2')}
					</h2>

					{hasErrors ? (
						<div className="ivirma-ivf-error-summary" role="alert">
							{t('due-date-calculator.error.summary')}
						</div>
					) : null}

					<ClayForm.Group className={errors.transferType ? 'has-error' : ''}>
						<label htmlFor="due-date-calculator-transfer-type">
							{t('due-date-calculator.form.transfer-type.label')}
						</label>
						<ClaySelect
							aria-describedby={
								errors.transferType
									? 'due-date-calculator-transfer-type-error'
									: undefined
							}
							aria-invalid={Boolean(errors.transferType)}
							id="due-date-calculator-transfer-type"
							onChange={(event) => setTransferType(event.target.value)}
							ref={transferTypeRef}
							value={transferType}
						>
							<option value="">
								{t('due-date-calculator.form.transfer-type.placeholder')}
							</option>
							{TRANSFER_TYPES.map((option) => (
								<option key={option.value} value={option.value}>
									{t(option.labelKey)}
								</option>
							))}
						</ClaySelect>
						{errors.transferType ? (
							<ClayForm.FeedbackGroup>
								<ClayForm.FeedbackItem id="due-date-calculator-transfer-type-error">
									{errors.transferType}
								</ClayForm.FeedbackItem>
							</ClayForm.FeedbackGroup>
						) : null}
					</ClayForm.Group>

					<ClayForm.Group className={errors.date ? 'has-error' : ''}>
						<label htmlFor="due-date-calculator-date">
							{t('due-date-calculator.form.date.label')}
						</label>
						<ClayDatePicker
							aria-describedby={
								errors.date ? 'due-date-calculator-date-error' : undefined
							}
							aria-invalid={Boolean(errors.date)}
							dateFormat="yyyy-MM-dd"
							id="due-date-calculator-date"
							onChange={setDateValue}
							placeholder={t('due-date-calculator.form.date.placeholder')}
							ref={dateFieldRef}
							spritemap={datePickerSpritemap}
							useNative={false}
							value={dateValue}
							years={{end: 9999, start: 1900}}
						/>
						{errors.date ? (
							<ClayForm.FeedbackGroup>
								<ClayForm.FeedbackItem id="due-date-calculator-date-error">
									{errors.date}
								</ClayForm.FeedbackItem>
							</ClayForm.FeedbackGroup>
						) : null}
					</ClayForm.Group>

					<ClayButton
						className="ivirma-ivf-button"
						displayType="primary"
						type="submit"
					>
						{t('due-date-calculator.action.calculate')}
					</ClayButton>
				</div>

				<div
					aria-hidden="true"
					className="ivirma-ivf-form-panel__pattern"
					style={{
						'--ivirma-ivf-banner-pattern-desktop': `url("${bannerPatterns.desktop}")`,
						'--ivirma-ivf-banner-pattern-mobile': `url("${bannerPatterns.mobile}")`,
					}}
				/>
				</form>

				<p className="ivirma-ivf-disclaimer">
					{t('due-date-calculator.disclaimer')}
				</p>
			</section>
		</div>
	);
}

class DueDateCalculatorElement extends HTMLElement {
	connectedCallback() {
		if (!this.root) {
			this.root = createRoot(this);
		}

		this.root.render(<App />);
	}

	disconnectedCallback() {
		if (this.root) {
			this.root.unmount();
			this.root = null;
		}
	}
}

if (!customElements.get(ELEMENT_ID)) {
	customElements.define(ELEMENT_ID, DueDateCalculatorElement);
}
